import { Fragment } from 'react';

import { useTranslation } from 'react-i18next';

import useBrains from '@/hooks/useBrains';
import { MessageBubble } from '@/modules/tryIt/components/Messages/MessageBuble/MessageBubble';
import { MessageNodeHeader } from '@/modules/tryIt/components/Messages/MessageNode/MessageNodeHeader';
import type { AmendPayload, NodeType } from '@/shared/types/tryIt';
import { noop } from '@/util/events';

import { TurnBlock } from './TurnBlock';

interface ConversationReplayProps {
  sessionNodes: NodeType[];
  sessionId: string;
  brainId: string;
  simulationContext: NonNullable<AmendPayload['simulation']>;
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export const ConversationReplay = ({
  sessionNodes,
  sessionId,
  brainId,
  simulationContext,
  selectedIndex,
  onSelect,
}: ConversationReplayProps) => {
  const { t } = useTranslation();
  const { brain } = useBrains(brainId);

  return (
    // [&_.response]:items-start anchors the bot-response icon to the top of
    // its row so it doesn't shift down by ~12px when the Amend button grows
    // the bubble height on selection.
    <div className="flex flex-col [&_.response]:items-start">
      {sessionNodes.map((node, index) => {
        const isActive = index === selectedIndex;
        // Only the selected turn with a real request_id and a loaded brain
        // language gets an amend payload: without them the server can't
        // route the correction correctly.
        const amendPayload: AmendPayload | undefined =
          isActive && node.request_id && brain?.language
            ? {
                request_id: node.request_id,
                task: 'synthesis',
                session_id: sessionId,
                language: brain.language,
                simulation: simulationContext,
              }
            : undefined;
        // Inactive turns with the same prereqs reserve the bottom space the
        // Amend button would occupy, so selecting another turn doesn't shift
        // the conversation vertically.
        const couldShowAmend = !!(node.request_id && brain?.language);
        const correction = node.corrections?.find((c) => c.task === 'synthesis');

        // Only stamp a synthetic key when the turn has no request_id;
        // real IDs stay stable across renders (important for amend state).
        return (
          <Fragment key={node.request_id ?? `sim-turn-${index}`}>
            <div className="flex items-center gap-2 px-4 py-2">
              <div className="h-px flex-1 bg-stroke" />
              <span className="text-xs text-foreground-muted">
                {t('simulations.turn_label', { 0: index + 1 })}
              </span>
              <div className="h-px flex-1 bg-stroke" />
            </div>

            <TurnBlock
              index={index}
              isActive={isActive}
              couldShowAmend={couldShowAmend}
              ariaLabel={t('simulations.select_turn', { 0: index + 1 })}
              onSelect={onSelect}
            >
              {node.author_type !== 'agent' && (
                <MessageNodeHeader
                  id={node.id}
                  brainId={brainId}
                  collection={node.collection}
                  index={index}
                  author_type={node.author_type}
                  steps={node.steps}
                  messages={node.messages}
                  name={node.name}
                  nodes_stack={node.nodes_stack}
                  broadcastId={node.broadcast_id}
                  campaignId={node.campaign_id}
                  broadcast_name={node.broadcast_name}
                  campaign_name={node.campaign_name}
                  isActive={isActive}
                />
              )}
              {node.messages.map((message) => (
                <MessageBubble
                  key={message.type}
                  message={message}
                  onOptionClick={noop}
                  onPostback={noop}
                  nodes_stack={node.nodes_stack}
                  brainId={brainId}
                  author_type={node.author_type}
                  author_id={node.author_id}
                  amendPayload={amendPayload}
                  correction={correction}
                />
              ))}
            </TurnBlock>
          </Fragment>
        );
      })}
      <div className="h-10 shrink-0" aria-hidden="true" />
    </div>
  );
};
